/**
 * ScormController
 * 
 * Wrapper for the SCORM API (2004, 3rd edition)
 * 
 * Adapted from scormfunctions.js in http://scorm.com/wp-content/assets/golf_examples/PIFS/RuntimeBasicCalls_SCORM20043rdEdition.zip
 * See 
 * http://scorm.com/scorm-explained/technical-scorm/golf-examples/
 * http://scorm.com/scorm-explained/technical-scorm/run-time/
 * http://scorm.com/scorm-explained/technical-scorm/run-time/run-time-reference/
 * 
 * Reinier Feijen, 2016-08-30
 * 
 */
export default class ScormController {
	constructor() {
		//There is actually no point in making the ScormController an instance 
		//(since all api calls are static and the controller does not have any state)
		//besides being able to inject it using soma.
		this._api = null;
		this._initialized = false;
		this._terminated = false;
	}

	/**
	 * Finds SCORM api and if found, calls API.initialize
	 */
	initialize() {
		//See http://scorm.com/scorm-explained/technical-scorm/run-time/
		if (!this._initialized) {
			//find scorm api and call initialize
			this._findAPI();
			if (this._api == null) {
				console.warn("ScormController.initialize: SCORM API not found.");
			}
			else {
				var result = this._api.Initialize("");
				if (result == Scorm.FALSE) {
					this._logError("ScormController.initialize failed: ");
				}
				else {
					this._initialized = true;
					console.log("ScormController.initialize: SCORM API found.");
					// Set SCORM completion status if no status set yet
					var completionStatus = this.getValue(Scorm.COMPLETION_STATUS);
					log.info("ScormController.initialize, current completion status:", completionStatus);
					if (completionStatus === Scorm.COMPLETION_STATUS_UNKNOWN) {
						this.setValue(Scorm.COMPLETION_STATUS, Scorm.COMPLETION_STATUS_INCOMPLETE);
						log.info("ScormController.initialize, setting completion status to:", Scorm.COMPLETION_STATUS_INCOMPLETE);
					}
				}
			}
		}
		return this._initialized;
	}

	/**
	 * Make sure this is called when game is closed (i.e. onbeforeunload)
	 **/
	endSession() {
		if (!this.isActive()) return;
		//just to be sure, we tell the LMS to persist our data
		var res = this.save();
		console.log("ScormController.endSession, save returned: " + res);
		//set the cmi.exit element to 'suspend', else all data will be wiped and not available in next session!
		res = this.setValue(Scorm.EXIT, Scorm.EXIT_SUSPEND);
		console.log("ScormController.endSession, setValue(Scorm.EXIT, Scorm.EXIT_SUSPEND) returned: " + res);
		//terminate the session
		res = this.terminate();
		console.log("ScormController.endSession, terminate returned: " + res);
	}

	terminate() {
		//See http://scorm.com/scorm-explained/technical-scorm/run-time/
		//Don't terminate if we haven't initialized or if we've already terminated
		if (!this.isActive()) return;
		var result = this._api.Terminate("");
		this._terminated = true;
		if (result == Scorm.FALSE) {
			this._logError("ScormController.terminate failed: ");
		}
		return result;
	}

	/**
	 * Calls API.Commit to persist all run-time data
	 * See paragraph 'Commit' on http://scorm.com/scorm-explained/technical-scorm/run-time/
	 * Probably, an LMS will do this anyway when terminating, but just call it to make sure.
	 */
	save() {
		if (!this.isActive()) return;
		var result = this._api.Commit("");
		if (result == Scorm.FALSE) {
			this._logError("ScormController.save (API.Commit) failed: ");
		}
		return result;
	}

	/**
	 * Retrieve data from LMS.
	 * @param {type} element See http://scorm.com/scorm-explained/technical-scorm/run-time/run-time-reference/
	 * @param {type} checkError There are situations where a GetValue call is expected to have an error and should not log an error.
	 * @returns {undefined|ScormController_L11.p.getValue@pro;_api@call;GetValue|ScormController.prototype.getValue.result}
	 */
	getValue(element, checkError) {
		if (!this.isActive()) return;
		var result = this._api.GetValue(element);

		if (checkError && result == "") {
			if (this.getError().errorNumber != Scorm.NO_ERROR) {
				this._logError("ScormController.getValue failed: ");
			}
		}

		if (result == Scorm.TRUE)
			result = true;
		else if (result == Scorm.FALSE)
			result = false;

		return result;
	}


	/**
 * Send completion parameters to LMS	 
 */
	complete() {
		console.log("[ScormController] Learner has completed the game!");

		// Sets score to 100%
		this.setValue(Scorm.SCORE_MIN, 0);
		this.setValue(Scorm.SCORE_MAX, 1);
		this.setValue(Scorm.SCORE_RAW, 1);
		this.setValue(Scorm.SCORE_SCALED, 1);

		// Completes the course.
		this.setValue(Scorm.COMPLETION_STATUS, Scorm.COMPLETION_STATUS_COMPLETED);
		this.setValue(Scorm.SUCCESS_STATUS, Scorm.SUCCESS_STATUS_PASSED);
	}

	/**
	 * Send data to LMS
	 * @param {type} element See http://scorm.com/scorm-explained/technical-scorm/run-time/run-time-reference/
	 * @param {type} value
	 * @returns {ScormController_L11.p.setValue@pro;_api@call;SetValue|ScormController.prototype.setValue.result|undefined}
	 */
	setValue(element, value) {
		if (!this.isActive()) return;
		var result = this._api.SetValue(element, value);
		if (result == Scorm.FALSE) {
			this._logError("ScormController.setValue failed: ");
		}
		return result;
	}

	/**
	 * Returns true if SCORM api is found
	 */
	isActive() {
		return this._initialized && !this._terminated;
	}

	getError() {
		//For error codes, see http://scorm.com/scorm-explained/technical-scorm/run-time/run-time-reference/
		var errorNumber = this._api.GetLastError();
		var errorString = this._api.GetErrorString(errorNumber);
		var diagnostic = this._api.GetDiagnostic(errorNumber);
		return { errorNumber: errorNumber, errorString: errorString, diagnostic: diagnostic };
	}

	_logError(prefix) {
		var err = this.getError();
		log.error(prefix + "'" + err.diagnostic + "' (" + err.errorNumber + ")");
	}

	_findAPI() {
		this._api = GetAPI(window);
	}
}

export class Scorm { }

Scorm.TRUE = "true";
Scorm.FALSE = "false";
Scorm.NO_ERROR = "0";

//String literals used in SCORM API. Extend as needed.
//See: http://scorm.com/scorm-explained/technical-scorm/run-time/run-time-reference/
Scorm.SUSPEND_DATA = "cmi.suspend_data";

Scorm.SCORE_MIN = "cmi.score.min";
Scorm.SCORE_MAX = "cmi.score.max";
Scorm.SCORE_RAW = "cmi.score.raw";
Scorm.SCORE_SCALED = "cmi.score.scaled";

Scorm.COMPLETION_STATUS = "cmi.completion_status";
Scorm.COMPLETION_STATUS_COMPLETED = "completed";
Scorm.COMPLETION_STATUS_INCOMPLETE = "incomplete";
Scorm.COMPLETION_STATUS_UNKNOWN = "unknown";

Scorm.SUCCESS_STATUS = "cmi.success_status";
Scorm.SUCCESS_STATUS_PASSED = "passed";

Scorm.LEARNER_ID = "cmi.learner_id";

Scorm.EXIT = "cmi.exit";
Scorm.EXIT_SUSPEND = "suspend";

// The GetAPI() function begins the process of searching for the LMS
// provided API Instance.  The function takes in a parameter that
// represents the current window.  The function is built to search in a
// specific order and stop when the LMS provided API Instance is found.
// The function begins by searching the current window’s parent, if the
// current window has a parent.  If the API Instance is not found, the
// function then checks to see if there are any opener windows.  If
// the window has an opener, the function begins to look for the
// API Instance in the opener window.
var GetAPI = function (win) {
	var api;
	if (win.parent != null && win.parent != win) {
		api = ScanForAPI(win.parent);
	}
	if (api == null && win.opener != null) {
		api = ScanForAPI(win.opener);
	}
	return api;
}

// The ScanForAPI() function searches for an object named API_1484_11
// in the window that is passed into the function.  If the object is
// found a reference to the object is returned to the calling function.
// If the instance is found the SCO now has a handle to the LMS
// provided API Instance.  The function searches a maximum number
// of parents of the current window.  If no object is found the
// function returns a null reference.  This function also reassigns a
// value to the win parameter passed in, based on the number of
// parents.  At the end of the function call, the win variable will be
// set to the upper most parent in the chain of parents.
var ScanForAPI = function (win) {
	try {
		var attempts = 0;
		while ((win.API_1484_11 == null) && (win.parent != null) && (win.parent != win)) {
			attempts++;
			if (attempts > 500) return null;
			win = win.parent;
		}
		return win.API_1484_11;
	} catch (err) {
		console.error(err);
	}
}